/**
 * Green Flag — MatchFillWidget slot recipe
 *
 * Stack of profile cards shown at the top of the Events page, prompting the
 * user to submit matches from a past event. The top card responds to swipe
 * gestures; the two bottom-center buttons are the primary affordances.
 */

import { defineSlotRecipe } from "@chakra-ui/react";

export const matchFillWidgetRecipe = defineSlotRecipe({
  className: "gf-match-fill",
  slots: [
    "root",
    "stack",
    "card",
    "image",
    "eventChip",
    "eventChipThumb",
    "eventChipText",
    "eventChipTitle",
    "eventChipDate",
    "actions",
    "actionButton",
    "donePrompt",
    "donePromptTitle",
    "donePromptActions",
    "fullscreen",
    "fullscreenBackdrop",
    "fullscreenHeader",
    "fullscreenScroll",
    "fullscreenImage",
    "fullscreenMeta",
  ],

  base: {
    root: {
      w: "full",
      px: "4",
    },
    stack: {
      position: "relative",
      w: "full",
      aspectRatio: "1 / 1",
      maxW: "420px",
      mx: "auto",
    },
    card: {
      position: "absolute",
      inset: 0,
      borderRadius: "2xl",
      overflow: "hidden",
      bg: "border.default",
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      willChange: "transform",
      touchAction: "pan-y",
    },
    image: {
      w: "full",
      h: "full",
      objectFit: "cover",
      display: "block",
      userSelect: "none",
      pointerEvents: "none",
    },
    eventChip: {
      position: "absolute",
      top: "10px",
      left: "10px",
      display: "inline-flex",
      alignItems: "center",
      gap: "0",
      padding: "1",
      paddingRight: "1",
      bg: "rgba(255,255,255,0.95)",
      borderRadius: "sm",
      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
      maxW: "calc(100% - 20px)",
      zIndex: 2,
      border: "none",
      cursor: "pointer",
      appearance: "none",
      transition: "padding 160ms ease, gap 160ms ease",
      "&[data-expanded='true']": {
        gap: "2.5",
        paddingRight: "3",
      },
    },
    eventChipThumb: {
      w: "40px",
      h: "40px",
      borderRadius: "xs",
      bg: "bg.subtle",
      backgroundSize: "cover",
      backgroundPosition: "center",
      flexShrink: 0,
    },
    eventChipText: {
      display: "flex",
      flexDirection: "column",
      minW: "0",
      lineHeight: "1.15",
    },
    eventChipTitle: {
      fontSize: "md",
      fontWeight: "semibold",
      color: "fg.default",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    eventChipDate: {
      fontSize: "2xs",
      fontWeight: "medium",
      letterSpacing: "widest",
      color: "fg.muted",
      textTransform: "uppercase",
      marginTop: "0.5",
    },
    actions: {
      position: "absolute",
      left: "0",
      right: "0",
      bottom: "-28px",
      display: "flex",
      justifyContent: "space-between",
      px: "10",
      zIndex: 10,
      pointerEvents: "none",
    },
    actionButton: {
      pointerEvents: "auto",
      w: "56px",
      h: "56px",
      borderRadius: "full",
      bg: "bg.canvas",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      border: "none",
      transition: "transform 120ms ease",
      _active: { transform: "scale(0.92)" },
    },
    donePrompt: {
      position: "absolute",
      inset: 0,
      borderRadius: "2xl",
      bg: "rgba(0,0,0,0.55)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "5",
      px: "6",
      zIndex: 20,
      textAlign: "center",
    },
    donePromptTitle: {
      color: "white",
      fontSize: "xl",
      fontWeight: "semibold",
      lineHeight: "1.2",
    },
    donePromptActions: {
      display: "flex",
      gap: "3",
    },
    fullscreen: {
      position: "fixed",
      inset: 0,
      zIndex: 1000,
    },
    fullscreenBackdrop: {
      position: "absolute",
      inset: 0,
      bg: "bg.canvas",
    },
    fullscreenHeader: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      display: "flex",
      alignItems: "center",
      h: "56px",
      px: "4",
      zIndex: 3,
    },
    fullscreenScroll: {
      position: "absolute",
      inset: 0,
      overflowY: "auto",
      pb: "8",
    },
    fullscreenImage: {
      w: "full",
      aspectRatio: "1 / 1",
      objectFit: "cover",
      display: "block",
    },
    fullscreenMeta: {
      px: "5",
      py: "4",
    },
  },
});
